import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { documentsApi } from 'domain/documents/api';
import { brainCategorizationEnabledSelector } from 'domain/documents/selectors/brainCategorizationEnabledSelector';
import type { SearchDoctype, SearchSubtypeChip } from 'domain/documents/schemas';
import { queryOptions } from 'services/query';

import { documentsKeys } from './keys';

type DocumentsAggregationsParams = {
  companyId: string;
  category: string;
  isConfidential: boolean;
  query: Record<string, unknown>;
};

const EMPTY_TABS: SearchDoctype[] = [];
const EMPTY_SUBTYPES: SearchSubtypeChip[] = [];

// documents/aggregations (da-19295 D9): doctype tabs + sub-type chips + active-category count
// under the GLOBAL filter only. companyId is in the KEY (per-company cache isolation — there is
// no cache reset on company switch, see domain/CLAUDE.md) but NOT in the request; the CompanyId
// header carries it.
export const documentsAggregationsQuery = ({ companyId, category, isConfidential, query }: DocumentsAggregationsParams) =>
  queryOptions({
    queryKey: documentsKeys.documentsAggregations(companyId, category, isConfidential, query),
    queryFn: () => documentsApi.getDocumentsAggregations({ query: { ...query, isConfidential, category } }),
  });

// Gated on the brain-categorization feature (experimental_brain ∧ Bookkeeper), like the sub-type
// catalog; disabled until a companyId is present. `extraEnabled` composes an extra condition (the
// accessor passes isGrid so the query fires only in grid view). Returns already-normalized arrays
// so consumers never touch `.data?` — `count` stays null while unknown (distinct from a real 0, so
// the deferred empty-redirect can't false-fire during load).
export const useDocumentsAggregations = (params: DocumentsAggregationsParams, extraEnabled = true) => {
  const featureEnabled = useSelector(brainCategorizationEnabledSelector);
  const { data } = useQuery({
    ...documentsAggregationsQuery(params),
    enabled: featureEnabled && extraEnabled && Boolean(params.companyId),
  });
  return {
    tabs: data?.tabs ?? EMPTY_TABS,
    subtypes: data?.subtypes ?? EMPTY_SUBTYPES,
    count: data?.count ?? null,
  };
};
